import {Card, TabProps, Tabs} from 'platform/components';
import {VStack} from 'platform/foundation';

import {defaultTo, find, propEq} from 'ramda';

import {GetCoreExchangeResponse} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {queryParams, Section} from '@dms/shared';

import {Nullish, RequiredTestIdProps, suffixTestId, useQueryState} from 'shared/web';

import {ItemsTab} from './components/ItemsTab';
import {ProcessMilestones} from './components/ProcessMilestones';
import {ReturnToSupplierTab} from './components/ReturnToSupplierTab';
import {StorageTab} from './components/StorageTab';

interface GeneralProps extends RequiredTestIdProps {
  coreExchange: GetCoreExchangeResponse | Nullish;
  onEditGeneral: VoidFunction;
  onEditReturnSettings: VoidFunction;
  isEditable: boolean;
}

export function General(props: GeneralProps) {
  const [activeTabId, setActiveTabId] = useQueryState(queryParams.COMPONENT_SECTIONS_TAB);

  const tabs: TabProps[] = [
    {
      id: 'items',
      title: i18n.t('entity.warehouse.labels.items'),
      content: (
        <ItemsTab
          items={props.coreExchange?.tabs.items}
          onEditGeneral={props.onEditGeneral}
          isEditable={props.isEditable}
          data-testid={suffixTestId('tabs.items', props)}
        />
      ),
    },
    {
      id: 'storage',
      title: i18n.t('entity.warehouse.labels.coreExchangeStorage'),
      content: (
        <StorageTab
          storage={props.coreExchange?.tabs.storage}
          data-testid={suffixTestId('tabs.storage', props)}
        />
      ),
    },
    {
      id: 'return-to-supplier',
      title: i18n.t('entity.warehouse.labels.coreExchangeReturnToSupplier'),
      content: (
        <ReturnToSupplierTab
          returnToSupplier={props.coreExchange?.tabs.returnToSupplier}
          onEditSettings={props.onEditReturnSettings}
          isEditable={props.isEditable}
          coreItemQuantity={
            find(propEq('CORE', 'itemType'), defaultTo([], props.coreExchange?.tabs.items.items))
              ?.quantity
          }
          data-testid={suffixTestId('tabs.returnToSupplier', props)}
        />
      ),
    },
  ];

  return (
    <Section isFullHeight data-testid={suffixTestId('wrapper', props)}>
      <VStack spacing={4}>
        <Card title={i18n.t('entity.warehouse.labels.coreExchangeCoreItemStatus')}>
          <ProcessMilestones
            milestones={props.coreExchange?.processMilestones}
            data-testid={suffixTestId('processMilestones', props)}
          />
        </Card>
        <Tabs
          isLazy
          variant="condensed"
          tabs={tabs}
          activeTabId={activeTabId}
          onChange={setActiveTabId}
        />
      </VStack>
    </Section>
  );
}
